import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import BetsButton from '../Vets/Button';
import { selectTokenBalance } from '../../redux/slices/walletSlice';
import { selectTokenBySymbol } from '../../redux/slices/tokenSlice';
import { Container, ModalContainer } from '../BaseModal';
import { formatUSD } from '../../common/currency';
import VetsTextInput from '../Vets/Inputs/Text';
import { useLazyGetSwapQuoteQuery } from '../../redux/api/authorized/swap';
import { selectPrivyToken } from '../../redux/slices/auth';
import { toTokenAmount } from '../../hooks/useSolana';
import * as yup from 'yup';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import BackButton from '../BackButton';

const StyledInput = styled.div`
  height: 70px;
  width: 100%;
  padding: 10px 20px;
  background-color: ${({ theme }) => theme.background.primary};
  border: 1px solid ${({ theme }) => theme.text.secondary};
  color: ${({ theme }) => theme.text.secondary};
  font-size: 18px;
  font-family: Manrope;
  font-weight: 400;
  text-align: right;
  display: flex;
  align-items: center;
  flex-direction: row-reverse;
`;

const InputContainer = styled.div`
  display: flex;
  width: fit-content;
  max-width: 719px;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  position: relative;
`;

const Label = styled.p`
  font-family: Manrope;
  color: ${({ theme }) => theme.text.primary};
  font-size: 12px;
  font-weight: 400;
`;

export const ErrorLabel = styled.p`
  margin-top: 5px;
  font-family: Manrope;
  color: ${({ theme }) => theme.status.error.primary};
  font-size: 12px;
  font-weight: 400;
  &::first-letter {
    text-transform: uppercase;
  }
`;

const ImageContainer = styled.div`
  position: absolute;
  top: 0px;
  bottom: 0px;
  left: 0px;
  padding: 0px 20px;
  display: flex;
  align-items: center;
  gap: 16px;
`;

const Image = styled.img`
  width: 36px;
  height: 36px;
`;

const NameContainer = styled.div``;

const Name = styled.p`
  color: ${({ theme }) => theme.text.regular};
  font-size: 20px;
  font-weight: 500;
  font-family: Manrope;
`;

const ValueContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: -5px;
`;

const SubValue = styled.p`
  color: ${({ theme }) => theme.text.secondary};
  font-size: 10px;
  font-family: Manrope;
  font-weight: 400;
`;

const UsdLabel = styled.p`
  margin-top: -3px;
  color: ${({ theme }) => theme.text.secondary};
  font-size: 14px;
  font-weight: 300;
  font-family: Manrope;
`;

const TokenInput = ({ label, value, subvalue, onClick }) => {
  return (
    <>
      <InputContainer onClick={onClick}>
        {label && <Label className='label'>{label}&nbsp;</Label>}
        <StyledInput>
          <ValueContainer>
            <p>{value}</p>
            {subvalue && <SubValue>{subvalue}</SubValue>}
          </ValueContainer>
        </StyledInput>
        <ImageContainer>
          <Image src='/vets.webp' />
          <NameContainer>
            <Name>VETS</Name>
            <UsdLabel>available</UsdLabel>
          </NameContainer>
        </ImageContainer>
      </InputContainer>
    </>
  );
};

const ButtonsContainer = styled.div`
  margin-top: 20px;
  width: 100%;
  display: flex;
  gap: 20px;
`;

const Title = styled.h1`
  color: ${({ theme }) => theme.text.title};
  font-size: 40px;
  font-weight: 600;
  font-family: Manrope;
`;

const USDToReceive = styled.p`
  color: ${({ theme }) => theme.text.regular};
  font-size: 12px;
  font-weight: 500;
  font-family: Manrope;
  margin-top: -5px;
`;

const SellVets = () => {
  const vetsBalance = useSelector((state) =>
    selectTokenBalance('solana', vetsToken?.address || '')(state),
  );
  const [tokenAmount, setTokenAmount] = useState(vetsBalance);
  const [vetsUsdBalance, setVetsUsdBalance] = useState(0);
  const [usdToReceive, setUsdToReceive] = useState(0);
  const [triggerGetSwapQuote] = useLazyGetSwapQuoteQuery();
  const navigate = useNavigate();
  const privyToken = useSelector(selectPrivyToken);

  const vetsToken = useSelector((state) =>
    selectTokenBySymbol(state, 'solana', 'VETS'),
  );

  const schema = yup
    .object({
      amount: yup
        .number()
        .min(1)
        .max(vetsBalance, 'Not enough VETs')
        .required('Sell amount is required')
        .typeError('Invalid sell amount'),
    })
    .required();

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    watch,
  } = useForm({
    mode: 'all',
    defaultValues: {
      amount: '0',
    },
    resolver: yupResolver(schema),
  });

  const watchedAmount = watch('amount');

  useEffect(() => {
    const fetchQuote = async () => {
      const resp = await triggerGetSwapQuote({
        inputMint: vetsToken?.address,
        outputMint: import.meta.env.VITE_SOLANA_USDC_MINT,
        amount: toTokenAmount(vetsBalance, 9),
      }).unwrap();
      const price = parseInt(resp.quote.outAmount) / 1000000;
      setVetsUsdBalance(price);
    };

    if (privyToken && vetsBalance && vetsToken?.address) {
      fetchQuote();
    }
  }, [triggerGetSwapQuote, privyToken, vetsBalance, vetsToken?.address]);

  useEffect(() => {
    const fetchQuote = async () => {
      const resp = await triggerGetSwapQuote({
        inputMint: vetsToken?.address,
        outputMint: import.meta.env.VITE_SOLANA_USDC_MINT,
        amount: toTokenAmount(watchedAmount, 9),
      }).unwrap();
      setUsdToReceive(resp.quote.outAmount / 1000000);
    };

    if (privyToken && watchedAmount && vetsToken?.address) {
      fetchQuote();
    }
  }, [
    triggerGetSwapQuote,
    privyToken,
    watchedAmount,
    setUsdToReceive,
    vetsToken?.address,
  ]);

  return (
    <Container>
      <ModalContainer>
        <BackButton />
        <div>
          <Title>Sell VETS</Title>
        </div>
        <div>
          <Controller
            name='amount'
            control={control}
            render={({ field: { onChange, onBlur, value } }) => (
              <VetsTextInput
                maxValue={vetsBalance}
                type='number'
                dark
                label='SELL AMOUNT'
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                error={errors.amount?.message}
              />
            )}
          />
        </div>
        <USDToReceive>~{usdToReceive} USD</USDToReceive>
        <TokenInput value={vetsBalance} subvalue={formatUSD(vetsUsdBalance)} />
        <ButtonsContainer>
          <BetsButton
            fullWidth
            disabled={!isValid || !vetsToken?.address}
            onClick={handleSubmit(async (data) => {
              navigate(`/app/vets/sell/confirm?tokenAmount=${data.amount}`);
            })}
          >
            Sell VETS
          </BetsButton>
        </ButtonsContainer>
      </ModalContainer>
    </Container>
  );
};

export default SellVets;
